import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { Tooltip } from './Tooltip';

type Variant = 'primary' | 'ghost' | 'on-dark' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

// DESIGN §5 Buttons: 6px radius (never pill), transform+background transitions, ease-out-quart.
const base =
  'inline-flex items-center justify-center gap-xs rounded-sm font-sans text-small font-medium ' +
  'transition-[background,transform] duration-almanac ease-almanac select-none ' +
  'focus-visible:shadow-focus disabled:opacity-40 disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  // Primary: Accent Blue → hover Ink Deep (no transform on hover, per DESIGN).
  primary: 'bg-accent-blue text-canvas-paper hover:bg-ink-deep',
  // Ghost: Canvas Paper, Accent Blue border+text → hover Canvas Sand + Ink Deep text.
  ghost:
    'bg-canvas-paper text-accent-blue border border-accent-blue hover:bg-canvas-sand hover:text-ink-deep',
  // On-Dark: for navy/accent surfaces.
  'on-dark': 'bg-canvas-paper text-ink-deep hover:bg-canvas-sand',
  // Danger: the single sanctioned error red, used for destructive confirms (PDPL erasure etc.).
  danger: 'bg-signal-error text-canvas-paper hover:opacity-90',
};

const sizes: Record<Size, string> = {
  sm: 'px-sm py-xs',
  md: 'px-lg py-[12px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, onClick, ...rest },
  ref,
) {
  const self = useRef<HTMLButtonElement | null>(null);
  // A control that becomes disabled WHILE FOCUSED drops focus to <body>, taking
  // the keyboard user's place in the page with it. The console's Save buttons do
  // exactly that on a successful save — the form is no longer dirty, so the
  // button disables itself under the pointer that pressed it — and Tab then
  // restarts from the top of the document.
  //
  // Only a button this user just pressed is restored, and only if nothing else
  // has taken focus in the meantime, so this can never steal focus from a
  // dialog, a toast or a field the screen moved to on purpose.
  const pressed = useRef(false);
  const isDisabled = disabled || loading;
  useEffect(() => {
    if (!isDisabled || !pressed.current) return;
    pressed.current = false;
    const el = self.current;
    // Either the browser has already dropped focus to <body> (Chrome, Firefox)
    // or it is still nominally on the now-disabled button (jsdom). Anything
    // else means focus has moved on purpose and is not ours to take.
    const active = document.activeElement;
    if (!el || (active !== document.body && active !== el)) return;
    // The button's own container: where Tab should carry on from, and the one
    // place that is certainly still on screen and still means the same thing.
    const holder = el.parentElement;
    if (!holder) return;
    if (!holder.hasAttribute('tabindex')) holder.setAttribute('tabindex', '-1');
    holder.focus({ preventScroll: true });
  }, [isDisabled]);

  // The other way this button takes the user's place with it: it is REMOVED.
  // The console's save bars render behind `{dirty && …}`, so a successful save
  // unmounts the very button that was pressed, and the browser drops focus to
  // <body> — Tab then restarts at the top of the document, past the whole
  // sidebar. Focus goes to the content region instead, which is where the
  // record the operator just saved still is.
  //
  // A layout-effect cleanup runs while the node is still in the tree, so it can
  // see that this button holds focus. The move itself waits a frame and only
  // happens if focus is still nowhere: a Radix dialog restores focus to its
  // trigger when it closes, and that must win over this.
  useLayoutEffect(() => {
    return () => {
      const el = self.current;
      const active = document.activeElement;
      // The button itself, or the container the effect above handed focus to
      // while the request was in flight — a save bar goes away with its button,
      // so that container is on its way out too.
      const held = !!el && !!active && active !== document.body && active.contains(el);
      if (!held) return;
      const main = document.querySelector('main');
      if (!main) return;
      requestAnimationFrame(() => {
        if (document.activeElement !== document.body) return;
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      });
    };
  }, []);

  const setRef = useCallback(
    (node: HTMLButtonElement | null) => {
      self.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  return (
    <button
      ref={setRef}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      onClick={(e) => {
        pressed.current = true;
        onClick?.(e);
      }}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-[14px] w-[14px] animate-spin rounded-pill border-2 border-current border-e-transparent"
    />
  );
}

/**
 * Action button gated by permission (spec §4): when not allowed it is DISABLED (not hidden)
 * and explains the missing permission via tooltip.
 */
export function PermissionButton({
  allowed,
  reason,
  children,
  disabled,
  ...rest
}: ButtonProps & { allowed: boolean; reason?: string | null }) {
  const btn = (
    <Button disabled={disabled || !allowed} {...rest}>
      {children}
    </Button>
  );
  if (allowed || !reason) return btn;
  // Wrap in a span so the tooltip works on a disabled button.
  return (
    <Tooltip content={reason}>
      <span className="inline-flex">{btn as ReactNode}</span>
    </Tooltip>
  );
}
